import { RefObject, useState } from "react";

interface DragDropProviderProps {
    onFilesDropped: (files: FileList) => void;
    fileInputRef: RefObject<HTMLInputElement>;
    isUploading: boolean;
    children: React.ReactNode;
}

export const DragDropProvider: React.FC<DragDropProviderProps> = ({
    onFilesDropped,
    fileInputRef,
    isUploading,
    children
}) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        if (
            x <= rect.left ||
            x >= rect.right ||
            y <= rect.top ||
            y >= rect.bottom
        ) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files?.length) {
            onFilesDropped(files);
        }
    };

    return (
        <div
            className="relative w-full h-full"
            onDragEnter={handleDragEnter}
            onDragOver={handleDrag}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {children}
            
            {isDragging && (
                <div className="absolute inset-0 bg-blue-500/10 z-50 rounded-lg">
                    <div className="absolute inset-4 border-2 border-dashed border-blue-500 rounded-lg bg-white/50 backdrop-blur-sm">
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <svg 
                                    className="mx-auto mb-4 w-12 h-12 text-blue-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                    />
                                </svg>
                                <p className="text-xl font-semibold text-gray-700">
                                    Drop your files here
                                </p>
                                <p className="text-gray-500 mt-2">
                                    Files will be uploaded immediately
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};